'use es6';

import { getFakeUndoableItemsInitialized, getFakeUndoableItems } from 'ContentEditorUI/redux/selectors/fakeUndoRedoSelectors';
import { FETCH_CONTENT_SCHEMA_SUCCEEDED, INITIALIZE_FAKE_UNDOABLE_ITEMS, UPDATE_FAKE_UNDOABLE_ITEMS, MODULE_REDUCER_CHANGE_UNDO, MODULE_REDUCER_CHANGE_REDO } from 'ContentEditorUI/redux/actions/actionTypes';
import { captureMessage } from 'ContentEditorUI/lib/exceptions';
import { isImmutable, getImmutableOrPlain } from '../../utils/dataHelpers';

/**
 * Each key represents some undoable state outside
 * of the undoable tree which can't easily be moved in.
 * Instead of relocating that state, we proxy updates
 * from these fake items.
 * @param {Object} fakeUndoableItems - object of shape {
 *   [key]: {
 *     get: reduxSelector,
 *     set: reduxAction,
 *     allowlistedActionTypes: mapOfActionTypes
 *     getActionArgs: (optional): function(nextValue, prevValue) {}. If omitted, nextValue is returned
 *   }
 * }
 */
const createFakeUndoRedoUpdatesMiddleware = fakeUndoableItems => {
  let ignoreActions = false;
  const allAllowlistedActionTypes = Object.keys(fakeUndoableItems).reduce((all, key) => Object.assign({}, all, fakeUndoableItems[key].allowlistedActionTypes), {});
  return store => next => action => {
    const currentState = store.getState();
    if (action.type === FETCH_CONTENT_SCHEMA_SUCCEEDED) {
      const result = next(action);
      const nextState = store.getState();
      store.dispatch({
        type: INITIALIZE_FAKE_UNDOABLE_ITEMS,
        items: Object.keys(fakeUndoableItems).reduce((items, key) => Object.assign({}, items, {
          [key]: fakeUndoableItems[key].get(nextState)
        }), {})
      });
      return result;
    } else if ([MODULE_REDUCER_CHANGE_UNDO, MODULE_REDUCER_CHANGE_REDO].includes(action.type)) {
      const prevItems = getFakeUndoableItems(currentState);
      const result = next(action);
      const nextItems = getFakeUndoableItems(store.getState());
      if (!nextItems) {
        captureMessage('nextItems is not defined', {
          extra: {
            prevItems,
            nextItems
          }
        });
      }
      if (nextItems && prevItems !== nextItems) {
        const entries = isImmutable(nextItems) ? nextItems.entrySeq() : Object.entries(nextItems);
        entries.forEach(([key, nextValue]) => {
          const prevValue = getImmutableOrPlain(prevItems, key);
          if (nextValue !== prevValue) {
            const args = fakeUndoableItems[key].getActionArgs ? fakeUndoableItems[key].getActionArgs(nextValue, prevValue) : [nextValue];
            const updateItemAction = fakeUndoableItems[key].set(...args);
            ignoreActions = true;
            store.dispatch(updateItemAction);
            ignoreActions = false;
          }
        });
      }
      return result;
    } else if (!ignoreActions && allAllowlistedActionTypes[action.type] && getFakeUndoableItemsInitialized(currentState)) {
      const result = next(action);
      const nextState = store.getState();
      const updatedItems = Object.keys(fakeUndoableItems).reduce((updated, key) => {
        if (fakeUndoableItems[key].allowlistedActionTypes[action.type]) {
          const prevItem = fakeUndoableItems[key].get(currentState);
          const nextItem = fakeUndoableItems[key].get(nextState);
          if (prevItem !== nextItem) {
            updated[key] = nextItem;
          }
        }
        return updated;
      }, {});
      if (Object.keys(updatedItems).length) {
        store.dispatch({
          type: UPDATE_FAKE_UNDOABLE_ITEMS,
          items: updatedItems
        });
      }
      return result;
    }
    return next(action);
  };
};
export default createFakeUndoRedoUpdatesMiddleware;